import { Badge, Box, Button, Flex, HStack, Stack, Text } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useCallback, useEffect, useState } from "react";
import { MdClose, MdExpandLess, MdExpandMore } from "react-icons/md";
import serverGenerationHubService, {
  ServerProgressUpdate,
} from "services/serverGenerationHubService";
import { ServerConfig, serverService } from "services/serverService";
import { getThemeColor } from "theme/colors";

const ServerProgressPanel = () => {
  const { t } = useTranslation();
  const [serversInProgress, setServersInProgress] = useState<ServerConfig[]>(
    [],
  );
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const loadServersInProgress = async () => {
    try {
      const allServers = await serverService.getAllServers();
      const inProgress = allServers.filter(
        (server) =>
          server.processingProgress !== undefined &&
          server.processingProgress < 100,
      );

      // Merge with existing state to preserve SignalR-updated fields
      setServersInProgress((prevServers) => {
        return inProgress.map((newServer) => {
          const existing = prevServers.find((s) => s.id === newServer.id);
          if (existing) {
            // Preserve SignalR-updated detailed fields, but use new values if they exist
            return {
              ...newServer,
              processedModels:
                newServer.processedModels ?? existing.processedModels,
              totalModels: newServer.totalModels ?? existing.totalModels,
              processedTools:
                newServer.processedTools ?? existing.processedTools,
              totalTools: newServer.totalTools ?? existing.totalTools,
            };
          }
          return newServer;
        });
      });

      setIsVisible(inProgress.length > 0);
    } catch (err) {
      console.error("Failed to load servers in progress:", err);
    }
  };

  // Stable callback for progress updates
  const handleProgressUpdate = useCallback((update: ServerProgressUpdate) => {
    setServersInProgress((prevServers) => {
      const updated = prevServers.map((server) =>
        server.id === update.serverId
          ? {
              ...server,
              processingProgress: update.progress,
              processedModels: update.processedModels,
              totalModels: update.totalModels,
              processedTools: update.processedTools,
              totalTools: update.totalTools,
            }
          : server,
      );

      // Remove servers that reached 100%
      const stillInProgress = updated.filter(
        (s) => s.processingProgress! < 100,
      );

      // Update visibility
      setIsVisible(stillInProgress.length > 0);

      return stillInProgress;
    });
  }, []);

  useEffect(() => {
    // Initialize SignalR connection
    const initializeSignalR = async () => {
      try {
        await serverGenerationHubService.start();
      } catch (err) {
        console.error(
          "Failed to start SignalR connection in progress panel:",
          err,
        );
      }
    };

    initializeSignalR();
    loadServersInProgress();

    // Poll for new servers every 10 seconds as a safety net
    const interval = setInterval(loadServersInProgress, 60000);

    // Reactively reload when a new generation is started anywhere in the app
    const handleGenerationStarted = async (serverId: string) => {
      await loadServersInProgress();
      serverGenerationHubService.subscribeToServer(
        serverId,
        handleProgressUpdate,
      );
    };

    serverGenerationHubService.onGenerationStarted(handleGenerationStarted);

    return () => {
      clearInterval(interval);
      serverGenerationHubService.offGenerationStarted(handleGenerationStarted);
    };
  }, [handleProgressUpdate]);

  // Subscribe to progress updates
  useEffect(() => {
    // Subscribe to all servers in progress
    serversInProgress.forEach((server) => {
      if (server.id) {
        serverGenerationHubService.subscribeToServer(
          server.id,
          handleProgressUpdate,
        );
      }
    });

    // Cleanup subscriptions
    return () => {
      serversInProgress.forEach((server) => {
        if (server.id) {
          serverGenerationHubService.unsubscribeFromServer(
            server.id,
            handleProgressUpdate,
          );
        }
      });
    };
  }, [serversInProgress.length, handleProgressUpdate]);

  const handleDismiss = (serverId: string) => {
    setServersInProgress((prev) => {
      const filtered = prev.filter((s) => s.id !== serverId);
      setIsVisible(filtered.length > 0);
      return filtered;
    });
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      w="full"
      bg={getThemeColor("progress.panelBg")}
      borderTopWidth="1px"
      borderColor={getThemeColor("progress.panelBorder")}
    >
      <Box px={4} py={3}>
        <Flex align="center" justify="space-between" mb={isCollapsed ? 0 : 2}>
          <HStack gap={2}>
            <Badge colorPalette="blue" size="sm">
              {serversInProgress.length}
            </Badge>
            <Text
              fontSize="xs"
              fontWeight="medium"
              color={getThemeColor("progress.text")}
            >
              {serversInProgress.length === 1
                ? t("app.serverProgress.generating")
                : t("app.serverProgress.multipleGenerating", {
                    count: serversInProgress.length,
                  })}
            </Text>
          </HStack>

          <Button
            size="xs"
            variant="ghost"
            onClick={() => setIsCollapsed(!isCollapsed)}
            colorPalette="blue"
          >
            {isCollapsed ? <MdExpandMore /> : <MdExpandLess />}
          </Button>
        </Flex>

        {!isCollapsed && (
          <Stack gap={2}>
            {serversInProgress.map((server) => (
              <Box
                key={server.id}
                bg={getThemeColor("progress.cardBg")}
                p={2}
                borderRadius="md"
                borderWidth="1px"
                borderColor={getThemeColor("progress.cardBorder")}
              >
                <Flex align="center" justify="space-between" mb={1}>
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    color={getThemeColor("text.primary")}
                    truncate
                    flex={1}
                  >
                    {server.name}
                  </Text>
                  <HStack gap={1}>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color={getThemeColor("progress.text")}
                    >
                      {server.processingProgress}%
                    </Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => handleDismiss(server.id!)}
                      colorPalette="gray"
                      title={t("app.serverProgress.dismiss")}
                      p={0}
                      minW={5}
                    >
                      <MdClose />
                    </Button>
                  </HStack>
                </Flex>

                <Box
                  w="full"
                  h="1"
                  bg={getThemeColor("progress.bg")}
                  borderRadius="full"
                  overflow="hidden"
                >
                  <Box
                    h="full"
                    bg={getThemeColor("progress.fill")}
                    w={`${server.processingProgress}%`}
                    transition="width 0.3s ease"
                  />
                </Box>

                {/* Detailed progress info */}
                {(server.totalModels || server.totalTools) && (
                  <Stack gap={1} mt={1.5}>
                    {server.totalModels !== undefined &&
                      server.totalModels > 0 && (
                        <Box w="full">
                          <HStack justify="space-between" mb={0.5}>
                            <Text
                              fontSize="2xs"
                              color={getThemeColor("text.secondary")}
                            >
                              {t("app.onboarding.step3.modelsProgress")}:
                            </Text>
                            <Text
                              fontSize="2xs"
                              fontWeight="medium"
                              color={getThemeColor("text.primary")}
                            >
                              {t("app.serverProgress.modelsProcessed", {
                                processed: server.processedModels || 0,
                                total: server.totalModels,
                              })}
                            </Text>
                          </HStack>
                          <Box
                            w="full"
                            h="1"
                            bg={getThemeColor("progress.bg")}
                            borderRadius="full"
                            overflow="hidden"
                          >
                            <Box
                              h="full"
                              bg={
                                server.processedModels === server.totalModels
                                  ? getThemeColor("progress.fillComplete").base
                                  : getThemeColor("progress.fill").base
                              }
                              w={`${((server.processedModels || 0) / server.totalModels) * 100}%`}
                              transition="width 0.3s ease, background-color 0.3s ease"
                            />
                          </Box>
                        </Box>
                      )}
                    {server.totalTools !== undefined &&
                      server.totalTools > 0 && (
                        <Box w="full">
                          <HStack justify="space-between" mb={0.5}>
                            <Text
                              fontSize="2xs"
                              color={getThemeColor("text.secondary")}
                            >
                              {t("app.onboarding.step3.toolsProgress")}:
                            </Text>
                            <Text
                              fontSize="2xs"
                              fontWeight="medium"
                              color={getThemeColor("text.primary")}
                            >
                              {t("app.serverProgress.toolsProcessed", {
                                processed: server.processedTools || 0,
                                total: server.totalTools,
                              })}
                            </Text>
                          </HStack>
                          <Box
                            w="full"
                            h="1"
                            bg={getThemeColor("progress.bg")}
                            borderRadius="full"
                            overflow="hidden"
                          >
                            <Box
                              h="full"
                              bg={
                                server.processedTools === server.totalTools
                                  ? getThemeColor("progress.fillComplete").base
                                  : getThemeColor("progress.fill").base
                              }
                              w={`${((server.processedTools || 0) / server.totalTools) * 100}%`}
                              transition="width 0.3s ease, background-color 0.3s ease"
                            />
                          </Box>
                        </Box>
                      )}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default ServerProgressPanel;
